import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, Play, Clock, BookOpen, Database, Wrench, GitBranch, Link, Timer, Shield, Lightbulb, Users, Target, MagnifyingGlass, FunnelSimple, MapPin, AndroidLogo, ChartBar, Mountains, RocketLaunch, Brain } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import { createPortal } from 'react-dom';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';

// Tutorial chapters configuration with updated icons and colors to match brain UI
const TUTORIAL_CHAPTERS = [
  {
    id: 'chapter-1',
    title: 'Meeting Your Laundry AI Brain',
    description: 'Get familiar with the interface and understand how your AI organizes knowledge',
    icon: Brain,
    color: 'bg-pink-100',
    textColor: 'text-pink-600',
    estimatedTime: '3 min',
    difficulty: 'Beginner',
    type: 'simple_answer' // Maps to brain system type
  },
  {
    id: 'chapter-2',
    title: 'Your First Teaching - Simple Answers',
    description: 'Learn to add basic Q&A responses like pricing and hours',
    icon: BookOpen,
    color: 'bg-blue-100',
    textColor: 'text-blue-600',
    estimatedTime: '5 min',
    difficulty: 'Beginner',
    type: 'simple_answer'
  },
  {
    id: 'chapter-3',
    title: 'Location Intelligence - Multiple Stores',
    description: 'Set up location-specific responses for different store locations',
    icon: MapPin,
    color: 'bg-gray-100',
    textColor: 'text-gray-600',
    estimatedTime: '7 min',
    difficulty: 'Intermediate',
    type: 'location'
  },
  {
    id: 'chapter-4',
    title: 'Complex Answers - Real-Time Lookups',
    description: 'Create dynamic responses that check live data like order status',
    icon: Database,
    color: 'bg-purple-100',
    textColor: 'text-purple-600',
    estimatedTime: '8 min',
    difficulty: 'Intermediate',
    type: 'complex_answer'
  },
  {
    id: 'chapter-5',
    title: 'Simple Actions - Making Things Happen',
    description: 'Set up automated actions like SMS notifications and emails',
    icon: Wrench,
    color: 'bg-amber-100',
    textColor: 'text-amber-700',
    estimatedTime: '6 min',
    difficulty: 'Intermediate',
    type: 'simple_action'
  },
  {
    id: 'chapter-6',
    title: 'Workflows - Complete Customer Journeys',
    description: 'Build complex multi-step processes like pickup scheduling',
    icon: GitBranch,
    color: 'bg-green-100',
    textColor: 'text-green-700',
    estimatedTime: '10 min',
    difficulty: 'Advanced',
    type: 'workflow'
  },
  {
    id: 'chapter-7',
    title: 'Integrations - Connecting Your Systems',
    description: 'Link your POS, payment systems, and other business tools',
    icon: Link,
    color: 'bg-indigo-100',
    textColor: 'text-indigo-600',
    estimatedTime: '8 min',
    difficulty: 'Advanced',
    type: 'integration'
  },
  {
    id: 'chapter-8',
    title: 'Post-Call Actions - Automatic Follow-ups',
    description: 'Set up tasks that happen automatically after customer calls',
    icon: Timer,
    color: 'bg-gray-100',
    textColor: 'text-gray-900',
    estimatedTime: '5 min',
    difficulty: 'Intermediate',
    type: 'post_call_action'
  },
  {
    id: 'chapter-9',
    title: 'The AI Assistant - Bulk Operations Magic',
    description: 'Use natural language to create multiple responses at once',
    icon: AndroidLogo,
    color: 'bg-blue-100',
    textColor: 'text-blue-600',
    estimatedTime: '4 min',
    difficulty: 'Beginner',
    type: 'ai_assistant'
  },
  {
    id: 'chapter-10',
    title: 'Conflict Detection - Consistency is Key',
    description: 'Learn to identify and resolve conflicting information',
    icon: Shield,
    color: 'bg-red-50',
    textColor: 'text-red-600',
    estimatedTime: '5 min',
    difficulty: 'Intermediate',
    type: 'conflict_detection'
  },
  {
    id: 'chapter-11',
    title: 'AI Coverage Analysis - Industry Specific',
    description: 'Monitor and improve your AI\'s knowledge coverage',
    icon: ChartBar,
    color: 'bg-white border border-gray-200',
    textColor: 'text-gray-600',
    estimatedTime: '6 min',
    difficulty: 'Advanced',
    type: 'coverage_analysis'
  },
  {
    id: 'chapter-12',
    title: 'Advanced Laundry Features',
    description: 'Seasonal services, commercial vs residential, and route optimization',
    icon: Mountains,
    color: 'bg-sky-100',
    textColor: 'text-sky-600',
    estimatedTime: '8 min',
    difficulty: 'Advanced',
    type: 'advanced_features'
  },
  {
    id: 'chapter-13',
    title: 'Real Laundromat Scenarios',
    description: 'Three complete implementation examples for different business types',
    icon: RocketLaunch,
    color: 'bg-rose-100',
    textColor: 'text-rose-600',
    estimatedTime: '12 min',
    difficulty: 'Advanced',
    type: 'scenarios'
  }
];

const TutorialChaptersModal = ({ isOpen, onClose, onStartChapter }) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const [completedChapters, setCompletedChapters] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [accountId, setAccountId] = useState(null);

  // Filter and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Initialize account and load progress
  useEffect(() => {
    if (isOpen) {
      initializeProgress();
    }
  }, [isOpen]);

  const initializeProgress = async () => {
    try {
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId: userAccountId } = await getUserAccountId();
      setAccountId(userAccountId);

      if (userAccountId) {
        await loadChapterProgress(userAccountId);
      }
    } catch (error) {
      console.error('Error initializing tutorial progress:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadChapterProgress = async (userAccountId) => {
    try {
      const { data, error } = await supabase
        .from('brain_tutorial_progress')
        .select('chapter_id, completed_at')
        .eq('account_id', userAccountId);

      if (error) {
        // Table might not exist yet, that's okay
        console.log('Tutorial progress table not found, starting fresh');
        return;
      }

      const completed = new Set(data.map(row => row.chapter_id));
      setCompletedChapters(completed);
    } catch (error) {
      console.error('Error loading chapter progress:', error);
    }
  };

  // Check if a chapter is available for use
  const isChapterAvailable = (chapterId) => {
    // Only chapter-1 is available for now
    return chapterId === 'chapter-1';
  };

  const handleStartChapter = async (chapter) => {
    // Don't allow starting unavailable chapters
    if (!isChapterAvailable(chapter.id)) {
      return;
    }

    try {
      // Mark chapter as started (not completed yet)
      if (accountId) {
        await supabase
          .from('brain_tutorial_progress')
          .upsert({
            account_id: accountId,
            chapter_id: chapter.id,
            started_at: new Date().toISOString()
          }, {
            onConflict: 'account_id,chapter_id'
          });
      }

      onStartChapter(chapter);
      onClose();
    } catch (error) {
      console.error('Error starting chapter:', error);
      // Continue anyway - don't block the tutorial
      onStartChapter(chapter);
      onClose();
    }
  };

  const getProgressStats = () => {
    const completed = completedChapters.size;
    const total = TUTORIAL_CHAPTERS.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  };

  // Filter and search logic
  const filteredChapters = TUTORIAL_CHAPTERS.filter(chapter => {
    const matchesSearch = searchQuery === '' || 
      chapter.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      chapter.description.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesDifficulty = selectedDifficulty === 'all' || 
      chapter.difficulty.toLowerCase() === selectedDifficulty.toLowerCase();

    return matchesSearch && matchesDifficulty;
  });

  // Animation variants matching RoadsPopup style
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  const popupVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        damping: 30, 
        stiffness: 300,
        duration: 0.4 
      }
    },
    exit: { 
      scale: 0.98, 
      opacity: 0,
      y: 10,
      transition: { duration: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({ 
      opacity: 1, 
      y: 0,
      transition: { 
        delay: i * 0.05,
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    })
  };

  if (!isOpen) return null;

  const { completed, total, percentage } = getProgressStats();

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={backdropVariants}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] p-4 overflow-hidden"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={onClose}
      >
        <motion.div
          variants={popupVariants}
          className="bg-white rounded-2xl shadow-xl w-full max-w-5xl overflow-hidden flex flex-col max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
          data-tutorial-chapters-modal="true"
        >
          {/* Header */}
          <motion.div 
            className="flex items-center justify-between px-5 sm:px-7 py-4 sm:py-5 border-b sticky top-0 bg-white z-10"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-gray-100/80 rounded-lg">
                <BookOpen size={16} className="text-gray-700 sm:w-[18px] sm:h-[18px]" />
                </div>
                <div>
                <h2 className="font-medium text-base sm:text-lg text-gray-900">Laundry AI Tutorial</h2>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                  Master your AI assistant step by step
                </p>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="rounded-full h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={16} className="sm:w-[18px] sm:h-[18px]" />
            </motion.button>
          </motion.div>

          {/* Filter and Search Bar */}
                <motion.div
            className="px-5 sm:px-7 py-3 sm:py-4 border-b bg-gray-50"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            <div className="flex items-center space-x-3">
              {/* Search Bar */}
              <div className="relative flex-1">
                <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search chapters..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>

              {/* Filter Dropdown */}
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className={cn(
                    "flex items-center space-x-2 px-3 py-2 border rounded-lg text-sm transition-colors",
                    selectedDifficulty === 'all' 
                      ? "border-gray-200 bg-white text-gray-700" 
                      : "border-gray-300 bg-gray-100 text-gray-900"
                  )}
                >
                  <FunnelSimple className="w-4 h-4" />
                  <span className="hidden sm:inline">
                    {selectedDifficulty === 'all' ? 'All Levels' : selectedDifficulty}
                  </span>
                </motion.button>

                <AnimatePresence>
                  {isFilterOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20"
                    >
                      {['all', 'beginner', 'intermediate', 'advanced'].map((level) => (
                        <motion.button
                          key={level}
                          whileHover={{ backgroundColor: '#f9fafb' }}
                          onClick={() => {
                            setSelectedDifficulty(level);
                            setIsFilterOpen(false);
                          }}
                          className={cn(
                            "w-full text-left px-3 py-2 text-sm transition-colors first:rounded-t-lg last:rounded-b-lg",
                            selectedDifficulty === level 
                              ? "bg-gray-100 text-gray-900" 
                              : "text-gray-700 hover:bg-gray-50"
                          )}
                        >
                          {level === 'all' ? 'All Levels' : level.charAt(0).toUpperCase() + level.slice(1)}
                        </motion.button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* Chapters Content */}
          <div className="p-5 sm:p-7 overflow-y-auto flex-1">
            {isLoading ? (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-center py-12"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="h-6 w-6 sm:h-8 sm:w-8 border-2 border-gray-300 border-t-gray-900 rounded-full"
                />
              </motion.div>
            ) : (
              <div className="space-y-1.5 sm:space-y-2">
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center justify-between mb-4"
                >
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700">
                    {filteredChapters.length} Chapter{filteredChapters.length !== 1 ? 's' : ''}
                    {searchQuery && ` matching "${searchQuery}"`}
                    {selectedDifficulty !== 'all' && ` (${selectedDifficulty})`}
                  </h3>
                </motion.div>

                <AnimatePresence>
                  {filteredChapters.length === 0 ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex flex-col items-center justify-center py-10 sm:py-12 px-4 
                        rounded-xl border border-dashed border-gray-200 text-center"
                    >
                      <BookOpen size={20} className="text-gray-300 mb-2 sm:mb-3 sm:w-6 sm:h-6" />
                      <p className="text-gray-500 text-xs sm:text-sm">
                        No chapters found
                      </p>
                      <p className="text-[10px] sm:text-xs text-gray-400 mt-1">
                        Try adjusting your search or filter
                      </p>
                    </motion.div>
                  ) : (
                    <motion.ul 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="rounded-xl overflow-hidden border border-gray-100 divide-y divide-gray-100"
                    >
                      <AnimatePresence>
                        {filteredChapters.map((chapter, index) => {
                  const isCompleted = completedChapters.has(chapter.id);
                  const isAvailable = isChapterAvailable(chapter.id);
                  const IconComponent = chapter.icon;

                  return (
                            <motion.li 
                      key={chapter.id}
                              custom={index}
                              variants={itemVariants}
                              initial="hidden"
                              animate="visible"
                              whileHover={isAvailable ? { backgroundColor: '#f9fafb' } : {}}
                              whileTap={isAvailable ? { scale: 0.995 } : {}}
                      className={cn(
                                "flex items-center justify-between px-3 sm:px-4 py-3 sm:py-4 bg-white transition-all duration-200 group relative",
                        isAvailable
                          ? isCompleted 
                            ? "bg-green-50/30 hover:bg-green-50/50 cursor-pointer" 
                            : "hover:bg-gray-50 cursor-pointer"
                          : "opacity-60 cursor-not-allowed"
                      )}
                      onClick={() => isAvailable && handleStartChapter(chapter)}
                    >
                              <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                      {/* Chapter Icon */}
                      <div className={cn(
                                  "flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center transition-colors",
                        isAvailable ? chapter.color : "bg-gray-100"
                      )}>
                                  <IconComponent className={cn(
                          "w-4 h-4 sm:w-5 sm:h-5", 
                          isAvailable ? chapter.textColor : "text-gray-400"
                        )} />
                      </div>

                      {/* Chapter Info */}
                                <div className="flex-1 min-w-0">
                                  {/* Chapter Number - NEW */}
                                  <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">
                                    CHAPTER {['ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN'][index]}
                                  </span>

                                  <div className="flex items-center space-x-2 sm:space-x-3 mb-1">
                                    <h3 className="font-medium text-sm sm:text-base text-gray-900 truncate">
                        {chapter.title}
                      </h3>
                                    {isCompleted && (
                                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                                    )}
                                  </div>
                                  <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                        {chapter.description}
                      </p>
                                  <div className="flex items-center space-x-2 sm:space-x-3 mt-2">
                                    <span className="text-[10px] sm:text-xs text-gray-500 flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {chapter.estimatedTime}
                          </span>
                          <span className={cn(
                                      "text-[10px] sm:text-xs px-2 py-1 rounded-full font-medium border",
                                      chapter.difficulty === 'Beginner' 
                                        ? "text-green-700 bg-green-50 border-green-200"
                                        : chapter.difficulty === 'Intermediate'
                                          ? "text-yellow-700 bg-yellow-50 border-yellow-200"
                                          : "text-red-700 bg-red-50 border-red-200"
                          )}>
                            {chapter.difficulty}
                          </span>
                        </div>
                                </div>
                              </div>

                              {/* Action Icon */}
                              <div className="flex-shrink-0 ml-3">
                                {!isAvailable ? (
                                  <span className="text-[10px] sm:text-xs text-gray-400 font-medium">Coming Soon</span>
                                ) : isCompleted ? (
                                  <span className="text-[10px] sm:text-xs text-green-600 font-medium">Completed</span>
                                ) : (
                                  <Play className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-gray-700 transition-colors" />
                                )}
                              </div>
                            </motion.li>
                  );
                })}
                      </AnimatePresence>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Footer with Progress Bar */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="px-5 sm:px-7 py-4 bg-gray-50 sticky bottom-0 border-t"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Progress</span>
              <span className="text-xs sm:text-sm text-gray-500">{completed}/{total} chapters completed</span>
              </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <motion.div
                className="bg-gradient-to-r from-gray-800 to-gray-900 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 1, delay: 0.5 }}
              />
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default TutorialChaptersModal; 